import { generatePath, matchPath } from "react-router-dom"
import qs from "query-string"
import merge from "lodash/merge"
import omit from "lodash/omit"
import { Roles } from "../../mp-utils/generic-utils"
import { REPORTING_ROUTE_MATCH } from "../constants"
import { FiltersState } from "../states"
import { NavItem, RouteParams } from "../types"
import { getRouteRolesByPathname } from "."
import { Role } from "../../mp-database"
import storage from "../../mp-core/helpers/storage"
import { isSuperAdminExcluded } from "./navAccess"

export { getRoleNavItems } from "./navAccess"

export const getCurrentNavItem = (
  items: NavItem[],
  page?: string,
  subpage?: string
) => {
  if (!page) {
    return null
  }

  const item = items.find(
    item => item.href === generatePath("/reporting/:page", { page })
  )

  if (subpage && item?.children) {
    return item.children.find(
      item =>
        item.href ===
        generatePath("/reporting/:page/:subpage", { page, subpage })
    )
  }

  return item
}

const getItemMatch = (item: NavItem, currentLocation: string) => {
  const currentMatch = matchPath<RouteParams>(currentLocation, {
    path: REPORTING_ROUTE_MATCH,
    exact: false
  })
  const itemMatch = matchPath<RouteParams>(item.href, {
    path: REPORTING_ROUTE_MATCH,
    exact: false
  })

  if (currentMatch && itemMatch) {
    const isSameModule = currentMatch.params.module === itemMatch.params.module
    const isSameSubmodule =
      currentMatch.params.submodule === itemMatch.params.submodule
    // Check an item without a subpage as a parent route match.
    // Example: `/reporting/teaching-tools/study-plan` should match
    // `/reporting/teaching-tools/study-plan/:subpage`.
    const isSameSubpage =
      !itemMatch.params.subpage ||
      currentMatch.params.subpage === itemMatch.params.subpage

    if (isSameModule && isSameSubpage && isSameSubmodule) {
      return currentMatch
    }
  }

  return null
}

export const checkSelectedChild = (
  item: NavItem,
  currentLocation: string,
  childOnly?: boolean,
  includesRouteMode?: boolean
): boolean => {
  const hasSelectedChildren = item.children
    ? item.children.some(subItem =>
        checkSelectedChild(subItem, currentLocation, includesRouteMode)
      )
    : false

  if (childOnly) {
    return hasSelectedChildren
  }

  if (
    currentLocation === item.href ||
    (currentLocation.includes(item.href) && includesRouteMode)
  ) {
    return hasSelectedChildren || true
  }

  const itemMatch = getItemMatch(item, currentLocation)

  if (itemMatch?.params?.entityId) {
    return true
  }

  return false
}

export const transformSideNavItems = (
  items: NavItem[],
  currentLocation: string,
  includesRouteMode?: boolean
): NavItem[] => {
  return items.map(item => {
    return {
      ...item,
      selected: checkSelectedChild(
        item,
        currentLocation,
        null,
        includesRouteMode
      ),
      children:
        item.children?.length > 0
          ? transformSideNavItems(
              item.children.filter(subItem => !!subItem.label),
              currentLocation,
              true
            )
          : undefined
    }
  })
}

export const findBreadcrumbItems = (
  navItems: NavItem[],
  currentLink: string,
  queryParams?: Record<string, string>
): NavItem[] => {
  for (const item of navItems) {
    if (item.href === currentLink) {
      return [item]
    }

    if (item.children?.length > 0) {
      const result = findBreadcrumbItems(
        item.children,
        currentLink,
        queryParams
      )

      if (result.length > 0) {
        return [item, ...result]
      }
    }

    const itemMatch = getItemMatch(item, currentLink)
    if (itemMatch) {
      return [item]
    }
  }

  return []
}

export const mergeSearchParams = (
  baseUrl: string,
  filters: FiltersState
): string => {
  const url = new URL(baseUrl, window.location.origin)
  const searchParams = qs.parse(url.search)

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && !searchParams[key]) {
      searchParams[key] = value
    }
  })

  url.search = qs.stringify(searchParams)

  return `${url.pathname}${url.search}`
}

export const getRedirectPathByNavItem = (
  navItem: NavItem,
  parentPath: string,
  userRole: Role
): string | null => {
  const parentNode = findParentNode(navItem, parentPath)

  if (!parentNode) {
    return null
  }

  return (
    parentNode.children
      ?.flatMap(node => getFirstAvailablePath(node, userRole))
      .find(Boolean) ||
    parentNode.href ||
    null
  )
}

const findParentNode = (node: NavItem, targetPath: string): NavItem | null => {
  if (
    node.href === targetPath ||
    (targetPath === "/reporting" && node.isRoot)
  ) {
    return node
  }

  return (
    node.children
      ?.flatMap(child => findParentNode(child, targetPath))
      .find(Boolean) || null
  )
}

const getFirstAvailablePath = (
  node: NavItem,
  userRole: Role
): string | null => {
  const nodeIsHidden =
    node?.key && storage.get(`dismiss-${node?.key}-nav`) === "true"

  if (
    !!node.href &&
    !nodeIsHidden &&
    !node.isEntity &&
    ((userRole === Roles.Super_Administrator &&
      !isSuperAdminExcluded(node.href)) ||
      getRouteRolesByPathname(node.href)?.includes(userRole))
  ) {
    return node.href
  }

  return (
    node.children
      ?.flatMap(node => getFirstAvailablePath(node, userRole))
      .find(Boolean) || null
  )
}

export const getOmitAndMergedSearchString = (args: {
  search: string
  omitKeys?: string[]
  mergeData: Record<string, string | string[]>
}): string => {
  const { search, omitKeys, mergeData } = args

  const restParams = omit(qs.parse(search || ""), omitKeys || [])

  const mergedParams = merge({}, restParams, mergeData)

  return qs.stringify(mergedParams, { arrayFormat: "repeat" })
}
